import { MARKS, type SimpleMark } from './brand-marks.ts';

// The shops, services and banks the app knows by name, each with its logo as a square tile like a
// banking app's: the brand's App Store icon or its own site's icon, fetched once with the owner's
// consent (28.09 and 30.09.2026, .dev/fetch-brand-icons.ts) and stored in the app as 128 px WebP
// files of about 3 kB, sharp on a phone screen; nothing leaves the device to look a merchant up. A
// brand without one gets its Simple Icons mark (CC0, brand-marks.ts) or its initial, white on the
// brand's colour (BrandLogo).

export type Brand = {
  readonly name: string;
  readonly hex: string;
  readonly image: string | null;
  readonly path: string | null;
  readonly initial: string | null;
};

const IMAGES = import.meta.glob<string>('../assets/brands/*.webp', { eager: true, import: 'default' });

const brand = (key: string, name: string, hex: string, mark?: SimpleMark): Brand => {
  const image = IMAGES[`../assets/brands/${key}.webp`] ?? null;
  return { name, hex: mark?.hex ?? hex, image, path: image ? null : (mark?.path ?? null), initial: image || mark ? null : name.charAt(0) };
};

/** First match wins: a name that shares a word with a later one comes first (Uber Eats before Uber). */
const BRANDS: readonly (readonly [RegExp, Brand])[] = [
  [/uber ?\*? ?eats/, brand('ubereats', 'Uber Eats', '06C167')],
  // A supermarket's petrol station ("Asda Petrol/ Upt 4155", "TESCO PFS 3456"): the shop's tile with
  // a fuel-pump badge.
  [/\basda\b.*\b(petrol|pfs|fuel)\b/, brand('asdapetrol', 'Asda Petrol', '68A51C')],
  [/\btesco\b.*\b(petrol|pfs|fuel)\b/, brand('tescopetrol', 'Tesco Petrol', '00539F')],
  [/\bsainsbury.*\b(petrol|pfs|fuel)\b/, brand('sainsburyspetrol', 'Sainsbury’s Petrol', 'F06C00')],
  [/\b(morrisons?|morr)\b.*\b(petrol|pfs|fuel)\b/, brand('morrisonspetrol', 'Morrisons Petrol', '00563F')],
  [/\btesco ?bank\b/, brand('tescobank', 'Tesco Bank', '00539F')],
  [/\btesco ?mobile\b/, brand('tescomobile', 'Tesco Mobile', '00539F')],
  [/\bprime ?video\b/, brand('primevideo', 'Prime Video', '1A98FF')],
  [/\baudible\b/, brand('audible', 'Audible', 'F8991C')],
  [/\bxbox\b/, brand('xbox', 'Xbox', '107C10')],
  [/\byoutube\b/, brand('youtube', 'YouTube', 'FF0000')],
  [/\bdavid lloyd\b/, brand('davidlloyd', 'David Lloyd', '1B365D')],
  [/\blloyds ?pharmacy\b/, brand('lloydspharmacy', 'LloydsPharmacy', '00843D')],
  [/\basda mobile\b/, brand('asdamobile', 'Asda Mobile', '68A51C')],

  // Groceries.
  [/\btesco\b/, brand('tesco', 'Tesco', '00539F')],
  [/\basda\b/, brand('asda', 'Asda', '68A51C')],
  [/\bsainsbury/, brand('sainsburys', 'Sainsbury’s', 'F06C00')],
  [/\bmorrisons?\b|\bmorr\b/, brand('morrisons', 'Morrisons', '00563F')],
  [/\baldi\b/, brand('aldi', 'Aldi', '00005F')],
  [/\blidl\b/, brand('lidl', 'Lidl', '0050AA')],
  [/\bco[- ]?op\b/, brand('coop', 'Co-op', '00B1E7')],
  [/\bwaitrose\b/, brand('waitrose', 'Waitrose', '5C8727')],
  [/\bm ?& ?s\b|marks (and|&) spencer/, brand('marksandspencer', 'M&S', '1E1E1E')],
  [/\biceland\b/, brand('iceland', 'Iceland', 'CC092F')],
  [/\bfarm ?foods\b/, brand('farmfoods', 'Farmfoods', 'E30613')],
  [/\bhome ?bargains\b/, brand('homebargains', 'Home Bargains', 'D71920')],
  [/\bb ?& ?m\b|\bbm ?stores\b/, brand('bandm', 'B&M', '5A2D82')],
  [/\bpoundland\b/, brand('poundland', 'Poundland', '00A651')],
  [/\bocado\b/, brand('ocado', 'Ocado', '4D2C7B')],
  [/\bspar\b/, brand('spar', 'Spar', '00843D')],
  [/\bheron ?foods\b/, brand('heronfoods', 'Heron Foods', 'E4002B')],
  [/\bcostco\b/, brand('costco', 'Costco', 'E31837')],
  [/\bnisa\b/, brand('nisa', 'Nisa', 'E30613')],
  [/\bcostcutter\b/, brand('costcutter', 'Costcutter', '5A2D82')],
  [/\bbooths\b/, brand('booths', 'Booths', '1E1E1E')],
  [/^one stop\b|\bone stop (stores?|\d)/, brand('onestop', 'One Stop', '00A0DF')],
  [/\blondis\b/, brand('londis', 'Londis', 'E30613')],
  [/\bbudgens\b/, brand('budgens', 'Budgens', '00843D')],
  [/\bpremier (stores?|express)\b/, brand('premierstores', 'Premier', '5A2D82')],
  [/\bwhole ?foods\b/, brand('wholefoods', 'Whole Foods', '00674B')],
  // The Polish shop in Bury: no logo anywhere online, so a jar of its own (.dev/brand-own). The bank
  // writes "Spizarnia"; typed with "ż" the letter drops out of the text ("spi arnia").
  [/\bspi ?z?arnia\b/, brand('spizarnia', 'Spiżarnia Bury', 'D4213D')],

  // Shops.
  [/\bamazon\b|\bamzn|\bkindle\b/, brand('amazon', 'Amazon', 'FF9900')],
  [/\bebay\b/, brand('ebay', 'eBay', 'E53238')],
  [/\bargos\b/, brand('argos', 'Argos', 'DA291C')],
  [/\bcurrys\b|\bpc ?world\b/, brand('currys', 'Currys', '4C12A1')],
  [/\bjohn lewis\b/, brand('johnlewis', 'John Lewis', '1E1E1E')],
  [/^next\b|\bnext (retail|directory|plc|online)\b/, brand('next', 'Next', '1E1E1E')],
  [/\bprimark\b/, brand('primark', 'Primark', '0DB4E4')],
  [/\bt ?k ?maxx\b/, brand('tkmaxx', 'TK Maxx', 'E4002B')],
  [/\bh ?& ?m\b|\bhennes\b/, brand('hm', 'H&M', 'E50010')],
  [/\bzara (uk|com|home|\d)|\binditex\b/, brand('zara', 'Zara', '1E1E1E')],
  [/\buniqlo\b/, brand('uniqlo', 'Uniqlo', 'FF0000')],
  [/\bjd ?sports\b/, brand('jdsports', 'JD Sports', '1E1E1E')],
  [/sports ?direct/, brand('sportsdirect', 'Sports Direct', '0E4DA4')],
  [/\bfrasers\b|\bhouse of fraser\b/, brand('frasers', 'Frasers', '1E1E1E')],
  [/\bnew look\b/, brand('newlook', 'New Look', '1E1E1E')],
  [/\briver island\b/, brand('riverisland', 'River Island', '1E1E1E')],
  [/\bmatalan\b/, brand('matalan', 'Matalan', '1E1E1E')],
  [/\bvery (co uk|com)\b/, brand('very', 'Very', 'E6007E')],
  [/\basos\b/, brand('asos', 'ASOS', '1E1E1E')],
  [/\bshein\b/, brand('shein', 'Shein', '1E1E1E')],
  [/\btemu\b/, brand('temu', 'Temu', 'FB7701')],
  [/\baliexpress\b/, brand('aliexpress', 'AliExpress', 'FF4747')],
  [/\betsy\b/, brand('etsy', 'Etsy', 'F16521')],
  [/\bvinted\b/, brand('vinted', 'Vinted', '007782')],
  [/\bonbuy\b/, brand('onbuy', 'OnBuy', '1D5DAB')],
  [/\bdeichmann\b/, brand('deichmann', 'Deichmann', '005AA0')],
  [/\bdecathlon\b/, brand('decathlon', 'Decathlon', '3643BA')],
  [/\bikea\b/, brand('ikea', 'IKEA', '0058A3')],
  [/\bdunelm\b/, brand('dunelm', 'Dunelm', '1E1E1E')],
  [/\bthe range\b|\bcds superstores\b/, brand('therange', 'The Range', '00843D')],
  [/\bb ?& ?q\b|\bb and q\b|\bdiy com\b/, brand('bandq', 'B&Q', 'F68B1F')],
  [/\bscrewfix\b/, brand('screwfix', 'Screwfix', '003D7C')],
  [/\btoolstation\b/, brand('toolstation', 'Toolstation', '003B73')],
  [/\bwickes\b/, brand('wickes', 'Wickes', '0055A5')],
  [/\bhomebase\b/, brand('homebase', 'Homebase', 'F26722')],
  [/\bhalfords\b/, brand('halfords', 'Halfords', 'F7931E')],
  [/\bboots\b/, brand('boots', 'Boots', '05054B')],
  [/\bsuperdrug\b/, brand('superdrug', 'Superdrug', 'E5007D')],
  [/\bholland (& |and )?barrett\b/, brand('hollandbarrett', 'Holland & Barrett', '00533F')],
  [/\bcohens chemist\b/, brand('cohens', 'Cohens Chemist', '00843D')],
  [/\bspecsavers\b/, brand('specsavers', 'Specsavers', '00664F')],
  [/\bvision express\b/, brand('visionexpress', 'Vision Express', '1D3C6E')],
  [/\bcard ?factory\b/, brand('cardfactory', 'Card Factory', 'E4007C')],
  [/\bw ?h ?smith\b/, brand('whsmith', 'WHSmith', '0D1C47')],
  [/\bwaterstones\b/, brand('waterstones', 'Waterstones', '1E1E1E')],
  [/\bryman\b/, brand('ryman', 'Ryman', 'E30613')],
  [/\bmax spielmann\b/, brand('maxspielmann', 'Max Spielmann', 'E30613')],
  [/\bsmyths\b/, brand('smyths', 'Smyths Toys', '004B93')],
  [/\bpets ?at ?home\b/, brand('petsathome', 'Pets at Home', '00AA28')],
  [/\bpost office\b/, brand('postoffice', 'Post Office', 'E4002B')],
  [/\bcex\b|\bwebuy\b/, brand('cex', 'CeX', 'E30613')],
  [/\bscan computers\b/, brand('scan', 'Scan', '0072BC')],
  [/\boverclockers\b|\bocuk\b/, brand('overclockers', 'Overclockers UK', 'E4002B')],
  [/\bapple\b|\bitunes\b|\bicloud\b/, brand('apple', 'Apple', '1E1E1E')],
  [/\bsamsung\b/, brand('samsung', 'Samsung', '1428A0')],
  [/\basus\b/, brand('asus', 'ASUS', '1E1E1E')],
  [/\bdell\b/, brand('dell', 'Dell', '007DB8')],
  [/\blenovo\b/, brand('lenovo', 'Lenovo', 'E2231A', MARKS.siLenovo)],
  [/\bcorsair\b/, brand('corsair', 'Corsair', '1E1E1E')],
  [/\brazer\b/, brand('razer', 'Razer', '00D100')],
  [/\bdyson\b/, brand('dyson', 'Dyson', '1E1E1E')],
  [/\bshark ?ninja\b/, brand('sharkninja', 'SharkNinja', '1E1E1E')],
  [/\biherb\b/, brand('iherb', 'iHerb', '458500')],
  [/\bnike\b/, brand('nike', 'Nike', '1E1E1E')],
  [/\badidas\b/, brand('adidas', 'adidas', '1E1E1E')],
  [/\bclarks\b/, brand('clarks', 'Clarks', '1E1E1E')],
  [/\bschuh\b/, brand('schuh', 'schuh', '1E1E1E')],
  [/\bfoot ?locker\b/, brand('footlocker', 'Foot Locker', '1E1E1E')],
  [/\bmountain ?warehouse\b/, brand('mountainwarehouse', 'Mountain Warehouse', '1D5DAB')],
  [/\blush\b/, brand('lush', 'Lush', '1E1E1E')],
  [/\bthe works\b/, brand('theworks', 'The Works', 'E4002B')],
  [/\bhobbycraft\b/, brand('hobbycraft', 'Hobbycraft', 'E6007E')],
  [/\brobert dyas\b/, brand('robertdyas', 'Robert Dyas', 'E4002B')],
  [/\bselfridges\b/, brand('selfridges', 'Selfridges', 'FFE500')],
  [/\bjysk\b/, brand('jysk', 'JYSK', '143C8C')],
  [/\bboohoo\b/, brand('boohoo', 'boohoo', '1E1E1E')],
  [/\bpretty ?little ?thing\b/, brand('prettylittlething', 'PrettyLittleThing', 'E6007E')],
  [/\bsuperdry\b/, brand('superdry', 'Superdry', '1E1E1E')],
  [/\bhollister\b/, brand('hollister', 'Hollister', '1D3C6E')],
  [/\blevi ?s\b|\blevi strauss\b/, brand('levis', 'Levi’s', 'C41230')],
  [/\btommy hilfiger\b/, brand('tommyhilfiger', 'Tommy Hilfiger', '1D3C6E')],
  [/^mango\b|\bmango (uk|online|\d)/, brand('mango', 'Mango', '1E1E1E')],
  [/\bpull ?(&|and) ?bear\b/, brand('pullbear', 'Pull&Bear', '1E1E1E')],
  [/\bbershka\b/, brand('bershka', 'Bershka', '1E1E1E')],
  [/\bskechers\b/, brand('skechers', 'Skechers', '1D3C6E')],
  [/\bnew balance\b/, brand('newbalance', 'New Balance', 'CF0A2C', MARKS.siNewbalance)],
  [/\bpuma\b/, brand('puma', 'PUMA', '1E1E1E')],
  [/\bnorth face\b/, brand('thenorthface', 'The North Face', '1E1E1E')],
  [/\bhomesense\b/, brand('homesense', 'Homesense', 'E4002B')],
  [/\bperfume shop\b/, brand('theperfumeshop', 'The Perfume Shop', '1E1E1E')],
  [/\blook ?fantastic\b/, brand('lookfantastic', 'LOOKFANTASTIC', '1E1E1E')],
  [/\bsephora\b/, brand('sephora', 'Sephora', '1E1E1E')],
  [/\bdfs\b/, brand('dfs', 'DFS', 'E4002B')],
  [/\bwayfair\b/, brand('wayfair', 'Wayfair', '7B189F')],
  [/\bao com\b|\bao retail\b/, brand('ao', 'AO', '00A0DF')],
  [/\bback ?market\b/, brand('backmarket', 'Back Market', '1E1E1E')],
  [/\bgumtree\b/, brand('gumtree', 'Gumtree', '72EF36')],
  [/\bmusic ?magpie\b/, brand('musicmagpie', 'musicMagpie', '1E1E1E')],
  [/\bjollyes\b/, brand('jollyes', 'Jollyes', 'E4002B')],
  [/\bzooplus\b/, brand('zooplus', 'zooplus', '00A0DF')],
  [/\blakeland\b/, brand('lakeland', 'Lakeland', '00843D')],
  [/\bgymshark\b/, brand('gymshark', 'Gymshark', '1E1E1E')],
  [/\bmy ?protein\b/, brand('myprotein', 'Myprotein', '00A0DF')],
  [/\bhuel\b/, brand('huel', 'Huel', '1E1E1E')],
  [/\broyal ?cd ?keys\b/, brand('royalcdkeys', 'Royal CD Keys', '5A2D82')],
  [/\bcd ?keys\b/, brand('cdkeys', 'CDKeys', '1E1E1E')],
  [/\bkinguin\b/, brand('kinguin', 'Kinguin', 'F29100')],
  [/\bg2a\b/, brand('g2a', 'G2A', 'F05F00')],
  [/\beneba\b/, brand('eneba', 'Eneba', '5A2D82')],

  // Eating out and takeaways.
  [/\bdeliveroo\b/, brand('deliveroo', 'Deliveroo', '00CCBC')],
  [/\bjust ?eat\b/, brand('justeat', 'Just Eat', 'FF8000')],
  [/\btoo ?good ?to ?go\b/, brand('toogoodtogo', 'Too Good To Go', '00615F')],
  [/\bmcdonald/, brand('mcdonalds', 'McDonald’s', 'DA291C')],
  [/\bkfc\b/, brand('kfc', 'KFC', 'A3080C')],
  [/\bburger ?king\b/, brand('burgerking', 'Burger King', 'D62300')],
  [/\bnando ?s?\b/, brand('nandos', 'Nando’s', 'DC291E')],
  [/\bpizza ?hut\b/, brand('pizzahut', 'Pizza Hut', 'EE3A43')],
  [/\bdomino ?s?\b/, brand('dominos', 'Domino’s', '006491')],
  [/\bpapa ?john/, brand('papajohns', 'Papa John’s', '00785A')],
  [/\bsubway\b/, brand('subway', 'Subway', '008C15')],
  [/\bfive guys\b/, brand('fiveguys', 'Five Guys', 'C8102E')],
  [/\btaco ?bell\b/, brand('tacobell', 'Taco Bell', '702082', MARKS.siTacobell)],
  [/\bpopeyes\b/, brand('popeyes', 'Popeyes', 'F25600')],
  [/\bgerman doner\b/, brand('germandoner', 'German Doner Kebab', '1E1E1E')],
  [/\bwagamama\b/, brand('wagamama', 'Wagamama', '1E1E1E')],
  [/wetherspoon/, brand('wetherspoon', 'Wetherspoon', '1D3C34')],
  [/\bgreggs\b/, brand('greggs', 'Greggs', '00558F')],
  [/\bcosta\b/, brand('costa', 'Costa', '6D1F37')],
  [/\bstarbucks\b/, brand('starbucks', 'Starbucks', '006241')],
  [/\bpret\b/, brand('pret', 'Pret', '862633')],
  [/\bcaff[a-z]* ?nero\b/, brand('caffenero', 'Caffè Nero', '1E1E1E')],
  [/\btim hortons?\b/, brand('timhortons', 'Tim Hortons', 'C8102E')],
  [/\bkrispy ?kreme\b/, brand('krispykreme', 'Krispy Kreme', '00704A')],
  [/\bpizza ?express\b/, brand('pizzaexpress', 'PizzaExpress', '1D3C6E')],
  [/\bzizzi\b/, brand('zizzi', 'Zizzi', '1E1E1E')],
  [/\bbella italia\b/, brand('bellaitalia', 'Bella Italia', '1D3C6E')],
  [/\btoby carvery\b/, brand('tobycarvery', 'Toby Carvery', '8B1E3F')],
  [/\bharvester\b/, brand('harvester', 'Harvester', '00843D')],
  [/\bhungry horse\b/, brand('hungryhorse', 'Hungry Horse', 'E4002B')],
  [/\bitsu\b/, brand('itsu', 'itsu', 'E4002B')],
  [/\bchopstix\b/, brand('chopstix', 'Chopstix', 'E4002B')],
  [/\bwingstop\b/, brand('wingstop', 'Wingstop', '00553F')],
  [/\bfrankie ?(&|and) ?benny/, brand('frankiebennys', 'Frankie & Benny’s', 'E4002B')],
  [/\bbeefeater\b/, brand('beefeater', 'Beefeater', '8B1E3F')],
  [/\bbrewers ?fayre\b/, brand('brewersfayre', 'Brewers Fayre', '1D3C6E')],
  [/\bmiller ?(&|and) ?carter\b/, brand('millercarter', 'Miller & Carter', '1E1E1E')],
  [/\ball bar one\b/, brand('allbarone', 'All Bar One', '1E1E1E')],
  [/\bwahaca\b/, brand('wahaca', 'Wahaca', 'E6007E')],
  [/\btortilla\b/, brand('tortilla', 'Tortilla', 'E4002B')],
  [/\bgbk\b|\bgourmet burger kitchen\b/, brand('gbk', 'GBK', '1E1E1E')],
  [/\bhonest burgers?\b/, brand('honestburgers', 'Honest Burgers', '1E1E1E')],
  [/\bshake ?shack\b/, brand('shakeshack', 'Shake Shack', '1E1E1E')],
  [/\bblack sheep coffee\b/, brand('blacksheepcoffee', 'Black Sheep Coffee', '1E1E1E')],
  [/\bjoe ?(&|and) ?the juice\b/, brand('joeandthejuice', 'JOE & THE JUICE', 'E6007E')],
  [/\bgails\b|\bgail s bakery\b/, brand('gails', 'GAIL’s', '1E1E1E')],
  [/\bpatisserie valerie\b/, brand('patisserievalerie', 'Patisserie Valerie', '1E1E1E')],

  // Getting about: rides, trains, buses, parking, fuel, the car.
  [/\buber\b/, brand('uber', 'Uber', '1E1E1E')],
  [/^bolt\b|\bbolt eu\b/, brand('bolt', 'Bolt', '34D186')],
  [/\blime ?\*? ?(ride|bike)\b/, brand('lime', 'Lime', '00DD00')],
  [/\btfl\b|\btransport for london\b/, brand('tfl', 'TfL', '113B92')],
  [/\bbee ?network\b|\btfgm\b|\bmetrolink\b/, brand('beenetwork', 'Bee Network', 'FFD400')],
  [/\btrainline\b/, brand('trainline', 'Trainline', '01C3A7')],
  [/\bnational ?rail\b/, brand('nationalrail', 'National Rail', 'E21836')],
  [/\bnorthern (rail|trains?|railway)\b/, brand('northern', 'Northern', '262262')],
  [/\bavanti\b/, brand('avanti', 'Avanti West Coast', '004354')],
  [/\btrans ?pennine\b/, brand('transpennine', 'TransPennine Express', '0B2344')],
  [/\blner\b/, brand('lner', 'LNER', 'CE0E2D')],
  [/\bstagecoach\b/, brand('stagecoach', 'Stagecoach', '1D5DAB')],
  [/\bfirst ?bus\b/, brand('firstbus', 'First Bus', 'E6007E')],
  [/\barriva\b/, brand('arriva', 'Arriva', '00B2A9')],
  [/\bnational ?express\b/, brand('nationalexpress', 'National Express', '0F1F4B')],
  [/\bflix ?bus\b/, brand('flixbus', 'FlixBus', '73D700')],
  [/pay ?by ?phone/, brand('paybyphone', 'PayByPhone', '0098D8')],
  [/\bring ?go\b/, brand('ringgo', 'RingGo', '1D5DAB')],
  [/\bjust ?park\b/, brand('justpark', 'JustPark', '2DB84B')],
  [/\bncp\b/, brand('ncp', 'NCP', '6C2C91')],
  [/\bq ?-? ?park\b/, brand('qpark', 'Q-Park', 'FFD100')],
  [/\beuro ?car ?parks?\b/, brand('eurocarparks', 'Euro Car Parks', '0055A5')],
  [/\brcp parking\b/, brand('rcpparking', 'RCP Parking', '1E1E1E')],
  [/\bshell\b/, brand('shell', 'Shell', 'FFD500')],
  [/\bbp\b/, brand('bp', 'bp', '009B3A')],
  [/\besso\b/, brand('esso', 'Esso', 'E4002B')],
  [/\btexaco\b/, brand('texaco', 'Texaco', 'E4002B')],
  [/\btesla\b/, brand('tesla', 'Tesla', 'CC0000')],
  [/\bhonda\b/, brand('honda', 'Honda', 'E40521', MARKS.siHonda)],
  [/\bkwik ?-? ?fit\b/, brand('kwikfit', 'Kwik Fit', '0055A5')],
  [/\bautodoc\b/, brand('autodoc', 'Autodoc', 'F36F21')],
  [/\bauto ?trader\b/, brand('autotrader', 'Autotrader', '1E1E1E')],
  [/\bthe aa\b|\baa (insurance|membership|breakdown)\b|\bautomobile association\b/, brand('theaa', 'AA', 'FFD300')],
  [/^rac\b|\brac (motoring|insurance|breakdown|ltd)\b/, brand('rac', 'RAC', 'F26722')],
  [/\bgreen ?flag\b/, brand('greenflag', 'Green Flag', '00A651')],
  [/\bfree ?now\b|\bmytaxi\b/, brand('freenow', 'FREENOW', 'E4002B')],
  [/\bmegabus\b/, brand('megabus', 'megabus', '1D3C6E')],
  [/\benterprise (rent|car|holdings|rac)/, brand('enterprise', 'Enterprise', '169A5A')],
  [/\bhertz\b/, brand('hertz', 'Hertz', 'FFD100')],
  [/\bavis\b/, brand('avis', 'Avis', 'D4002A')],
  [/\beuropcar\b/, brand('europcar', 'Europcar', '00953B')],
  [/\bsixt\b/, brand('sixt', 'SIXT', 'FF5F00')],
  [/\bzipcar\b/, brand('zipcar', 'Zipcar', '3D8A2C')],
  [/\bapcoa\b/, brand('apcoa', 'APCOA', '0055A5')],
  [/\bmerseyflow\b|\bmersey gateway\b/, brand('merseyflow', 'Merseyflow', '0055A5')],
  [/\bmotorpoint\b/, brand('motorpoint', 'Motorpoint', '1D3C6E')],
  [/\barnold clark\b/, brand('arnoldclark', 'Arnold Clark', '1D3C6E')],
  [/\bapplegreen\b/, brand('applegreen', 'Applegreen', '00A651')],
  [/\bmoto (hospitality|services|\d)/, brand('moto', 'Moto', 'E4002B')],
  [/\bwelcome ?break\b/, brand('welcomebreak', 'Welcome Break', 'E4002B')],
  [/\broadchef\b/, brand('roadchef', 'Roadchef', '1D3C6E')],
  [/\bmfg\b|\bmotor fuel group\b/, brand('mfg', 'MFG', '1D3C6E')],
  [/\bionity\b/, brand('ionity', 'IONITY', '1E1E1E')],
  [/\binstavolt\b/, brand('instavolt', 'InstaVolt', '1E1E1E')],
  [/\bpod ?point\b/, brand('podpoint', 'Pod Point', '1E1E1E')],
  [/\bgridserve\b/, brand('gridserve', 'GRIDSERVE', '1E1E1E')],
  [/\bmipermit\b/, brand('mipermit', 'MiPermit', '00A0DF')],
  [/\bgwr\b|\bgreat western railway\b/, brand('gwr', 'GWR', '0B2E2E')],
  [/\bthameslink\b/, brand('thameslink', 'Thameslink', 'E6007E')],
  [/\bsouthern (railway|rail)\b/, brand('southernrail', 'Southern', '00843D')],
  [/\bsouth western railway\b|\bswr\b/, brand('swr', 'South Western Railway', '1D3C6E')],
  [/\bsoutheastern\b/, brand('southeastern', 'Southeastern', '1D3C6E')],
  [/\bscotrail\b/, brand('scotrail', 'ScotRail', '1D3C6E')],
  [/\btransport for wales\b|\btfw\b/, brand('tfw', 'Transport for Wales', 'E4002B')],
  [/\bwest midlands (railway|trains)\b/, brand('wmr', 'West Midlands Railway', 'F39200')],
  [/\beast midlands railway\b|\bemr\b/, brand('emr', 'East Midlands Railway', '5A2D82')],
  [/\bchiltern railways?\b/, brand('chiltern', 'Chiltern Railways', '1D3C6E')],
  [/\blumo\b/, brand('lumo', 'Lumo', '1D3C6E')],
  [/\bmerseyrail\b/, brand('merseyrail', 'Merseyrail', 'FFD100')],
  [/\bgreater anglia\b/, brand('greateranglia', 'Greater Anglia', 'E4002B')],
  [/\bcross ?country\b/, brand('crosscountry', 'CrossCountry', '8B1E3F')],
  [/\bheathrow express\b/, brand('heathrowexpress', 'Heathrow Express', '5A2D82')],
  [/\baddison lee\b/, brand('addisonlee', 'Addison Lee', '1E1E1E')],
  [/\bveezu\b/, brand('veezu', 'Veezu', '1D3C6E')],
  [/\bmanchester airport\b|\bmag parking\b/, brand('manchesterairport', 'Manchester Airport', '1D3C6E')],
  [/\bheathrow\b/, brand('heathrow', 'Heathrow', '5A2D82')],
  [/\bgatwick\b/, brand('gatwick', 'Gatwick', '1D3C6E')],

  // Travel.
  [/\bbritish ?airways\b/, brand('britishairways', 'British Airways', '2E5C99')],
  [/\bvirgin ?atlantic\b/, brand('virginatlantic', 'Virgin Atlantic', 'DA0530')],
  [/\bryanair\b/, brand('ryanair', 'Ryanair', '073590')],
  [/\beasyjet\b/, brand('easyjet', 'easyJet', 'FF6600')],
  [/\bjet ?2/, brand('jet2', 'Jet2', 'E4002B')],
  [/\bwizz ?air\b/, brand('wizzair', 'Wizz Air', 'C6007E')],
  [/\blot polish\b|\blot airlines\b|\bpolskie linie lotnicze\b/, brand('lot', 'LOT', '11397E')],
  [/\blufthansa\b/, brand('lufthansa', 'Lufthansa', '05164D')],
  [/\bklm\b/, brand('klm', 'KLM', '00A1DE')],
  [/\bemirates\b/, brand('emirates', 'Emirates', 'D71921')],
  [/\btui\b/, brand('tui', 'TUI', 'D40E14')],
  [/\beurostar\b/, brand('eurostar', 'Eurostar', '0A1A45')],
  [/\bbooking ?com\b/, brand('booking', 'Booking.com', '003A9A')],
  [/\bexpedia\b/, brand('expedia', 'Expedia', '191E3B')],
  [/\bhotels ?com\b/, brand('hotelscom', 'Hotels.com', 'EF3346')],
  [/\bairbnb\b/, brand('airbnb', 'Airbnb', 'FF5A5F')],
  [/\bpremier ?inn\b/, brand('premierinn', 'Premier Inn', '4B1F68')],
  [/\btravelodge\b/, brand('travelodge', 'Travelodge', '003C71')],
  [/^hilton\b|\bhilton (hotels?|honors|garden)\b/, brand('hilton', 'Hilton', '104C97')],
  [/\bmarriott\b/, brand('marriott', 'Marriott', 'A70023')],
  [/\blove ?holidays\b/, brand('loveholidays', 'loveholidays', 'E6007E')],
  [/\bon the beach\b/, brand('onthebeach', 'On the Beach', '00A0DF')],
  [/\btrip ?com\b/, brand('tripcom', 'Trip.com', '287DFA')],
  [/\bagoda\b/, brand('agoda', 'Agoda', '1E1E1E')],
  [/\bihg\b|\bholiday inn\b|\bcrowne plaza\b/, brand('ihg', 'IHG (Holiday Inn)', '1E1E1E')],
  [/\bcent(er|re) parcs\b/, brand('centerparcs', 'Center Parcs', '00843D')],
  [/\bhaven (holidays|com)\b/, brand('haven', 'Haven', '1D3C6E')],
  [/\bbutlins\b/, brand('butlins', 'Butlins', 'E4002B')],
  [/\baer ?lingus\b/, brand('aerlingus', 'Aer Lingus', '00843D')],
  [/\bturkish airlines\b/, brand('turkishairlines', 'Turkish Airlines', 'C8102E')],
  [/\bqatar airways\b/, brand('qatarairways', 'Qatar Airways', '5C0632')],
  [/\bnorwegian air\b/, brand('norwegian', 'Norwegian', 'D81939')],
  [/\bvueling\b/, brand('vueling', 'Vueling', 'FFCC00')],

  // Subscriptions, games and apps.
  [/\bnetflix\b/, brand('netflix', 'Netflix', 'E50914')],
  [/\bdisney\b/, brand('disney', 'Disney+', '0E2D6D')],
  [/\bnow ?tv\b/, brand('nowtv', 'NOW', '1E1E1E')],
  [/\bparamount\b/, brand('paramount', 'Paramount+', '0064FF')],
  [/\bcrunchyroll\b/, brand('crunchyroll', 'Crunchyroll', 'FF5E00')],
  [/\bitvx?\b/, brand('itvx', 'ITVX', '1E1E1E')],
  [/\btidal\b/, brand('tidal', 'TIDAL', '1E1E1E')],
  [/\btnt sports\b|\bbt sport\b/, brand('tntsports', 'TNT Sports', '1E1E1E')],
  [/\bdiscovery ?plus\b/, brand('discoveryplus', 'discovery+', '0061FF')],
  [/\bmubi\b/, brand('mubi', 'MUBI', '1E1E1E')],
  [/\bzoom (us|video|com|communications)\b/, brand('zoom', 'Zoom', '0B5CFF')],
  [/\bslack\b/, brand('slack', 'Slack', '4A154B')],
  [/\bfigma\b/, brand('figma', 'Figma', '1E1E1E')],
  [/\bjetbrains\b/, brand('jetbrains', 'JetBrains', '1E1E1E')],
  [/\bcloudflare\b/, brand('cloudflare', 'Cloudflare', 'F38020')],
  [/\bovh ?cloud\b|\bovh\b/, brand('ovhcloud', 'OVHcloud', '000E9C')],
  [/\bdigital ?ocean\b/, brand('digitalocean', 'DigitalOcean', '0080FF')],
  [/\bnamecheap\b/, brand('namecheap', 'Namecheap', 'DE3723', MARKS.siNamecheap)],
  [/\bgo ?daddy\b/, brand('godaddy', 'GoDaddy', '1BDBDB')],
  [/\bhostinger\b/, brand('hostinger', 'Hostinger', '673DE6')],
  [/\bionos\b/, brand('ionos', 'IONOS', '003D8F')],
  [/\bmidjourney\b/, brand('midjourney', 'Midjourney', '1E1E1E')],
  [/\beleven ?labs\b/, brand('elevenlabs', 'ElevenLabs', '1E1E1E')],
  [/\bcursor\b|\banysphere\b/, brand('cursor', 'Cursor', '1E1E1E')],
  [/\bsuno\b/, brand('suno', 'Suno', '1E1E1E')],
  [/\blinkvertise\b/, brand('linkvertise', 'Linkvertise', 'F39200')],
  [/\bspotify\b/, brand('spotify', 'Spotify', '1DB954')],
  [/\bdeezer\b/, brand('deezer', 'Deezer', 'A238FF')],
  [/\bsoundcloud\b/, brand('soundcloud', 'SoundCloud', 'FF5500')],
  [/\bplaystation\b|\bpsn\b/, brand('playstation', 'PlayStation', '0070D1')],
  [/\bnintendo\b/, brand('nintendo', 'Nintendo', 'E60012')],
  [/\bsteam\b/, brand('steam', 'Steam', '1B2838')],
  [/\bepic ?games\b/, brand('epicgames', 'Epic Games', '313131', MARKS.siEpicgames)],
  [/\bgog\b/, brand('gog', 'GOG', '86328A')],
  [/\bhumble ?bundle\b/, brand('humble', 'Humble Bundle', 'CC2929')],
  [/\belectronic arts\b|\bea (app|play|games|com)\b/, brand('ea', 'EA', 'FF4747')],
  [/\bactivision\b/, brand('activision', 'Activision', '1E1E1E')],
  [/\bblizzard\b|\bbattle ?net\b/, brand('blizzard', 'Battle.net', '148EFF')],
  [/\bubisoft\b/, brand('ubisoft', 'Ubisoft', '1E1E1E')],
  [/\broblox\b/, brand('roblox', 'Roblox', '1E1E1E')],
  [/\btwitch\b/, brand('twitch', 'Twitch', '9146FF')],
  [/\bdiscord\b/, brand('discord', 'Discord', '5865F2')],
  [/\bpatreon\b/, brand('patreon', 'Patreon', 'FF424D')],
  [/\bonlyfans\b/, brand('onlyfans', 'OnlyFans', '00AFF0', MARKS.siOnlyfans)],
  [/\bgoogle\b/, brand('google', 'Google', '4285F4')],
  [/\bmicrosoft\b/, brand('microsoft', 'Microsoft', '737373')],
  [/\bclaude\b|\banthropic\b/, brand('claude', 'Claude', 'D97757')],
  [/\bopenai\b|\bchatgpt\b/, brand('openai', 'OpenAI', '1E1E1E')],
  [/\bperplexity\b/, brand('perplexity', 'Perplexity', '1FB8CD')],
  [/\bgithub\b/, brand('github', 'GitHub', '181717')],
  [/\badobe\b/, brand('adobe', 'Adobe', 'FA0F00')],
  [/\bcanva\b/, brand('canva', 'Canva', '00C4CC')],
  [/\bnotion\b/, brand('notion', 'Notion', '1E1E1E')],
  [/\bdropbox\b/, brand('dropbox', 'Dropbox', '0061FF')],
  [/\bgrammarly\b/, brand('grammarly', 'Grammarly', '027E6F', MARKS.siGrammarly)],
  [/\bduolingo\b/, brand('duolingo', 'Duolingo', '58CC02')],
  [/\blinkedin\b/, brand('linkedin', 'LinkedIn', '0A66C2')],
  [/\btwitter\b|\bx corp\b/, brand('x', 'X', '1E1E1E')],
  [/\bfacebk\b|\bfacebook\b|\binstagram\b|\bmeta (platforms|verified)\b/, brand('meta', 'Meta', '0467DF', MARKS.siMeta)],
  [/\btinder\b/, brand('tinder', 'Tinder', 'FF6B6B')],
  [/\bbumble\b/, brand('bumble', 'Bumble', 'FFC629')],
  [/\bhinge\b/, brand('hinge', 'Hinge', '1E1E1E')],
  [/\bstrava\b/, brand('strava', 'Strava', 'FC4C02')],
  [/\bheadspace\b/, brand('headspace', 'Headspace', 'F47D31')],
  [/\bpeloton\b/, brand('peloton', 'Peloton', '1E1E1E')],
  [/\bgarmin\b/, brand('garmin', 'Garmin', '1E1E1E')],
  [/\bnord ?vpn\b/, brand('nordvpn', 'NordVPN', '4687FF')],
  [/\bexpress ?vpn\b/, brand('expressvpn', 'ExpressVPN', 'DA3940')],
  [/\bproton\b/, brand('proton', 'Proton', '6D4AFF')],
  [/\b1 ?password\b/, brand('onepassword', '1Password', '145FE4')],

  // Phone, broadband, TV.
  [/\bvoxi\b/, brand('voxi', 'VOXI', '1E1E1E')],
  [/\bvodafone\b/, brand('vodafone', 'Vodafone', 'E60000')],
  [/\bo2\b/, brand('o2', 'O2', '0019A5')],
  [/\bee\b/, brand('ee', 'EE', '007B85')],
  [/\bthree (uk|mobile|co uk)\b|\bhutchison 3g\b|\bh3g\b/, brand('three', 'Three', '1E1E1E')],
  [/\bsmarty\b/, brand('smarty', 'SMARTY', '1E1E1E')],
  [/\bgiffgaff\b/, brand('giffgaff', 'giffgaff', '1E1E1E')],
  [/\blebara\b/, brand('lebara', 'Lebara', '0033A0')],
  [/\blyca ?mobile\b/, brand('lyca', 'Lyca Mobile', '0066B3')],
  [/\bid mobile\b/, brand('idmobile', 'iD Mobile', 'E4002B')],
  [/^bt\b|\bbt (group|plc|com|broadband|mobile|sport)\b/, brand('bt', 'BT', '6400AA')],
  [/\bplus ?net\b/, brand('plusnet', 'Plusnet', 'E6007E')],
  [/\btalk ?talk\b/, brand('talktalk', 'TalkTalk', '6D2077')],
  [/\bsky\b/, brand('sky', 'Sky', '0072C9')],
  [/\bvirgin ?media\b/, brand('virginmedia', 'Virgin Media', 'ED1A3B')],
  [/\bhyperoptic\b/, brand('hyperoptic', 'Hyperoptic', 'E6007E')],
  [/\bcommunity fibre\b/, brand('communityfibre', 'Community Fibre', '1E1E1E')],
  [/\btv ?licen[cs]/, brand('tvlicensing', 'TV Licensing', 'E4002B')],
  [/\btalkmobile\b/, brand('talkmobile', 'Talkmobile', '1D3C6E')],
  [/\butility warehouse\b|\btelecom plus\b/, brand('utilitywarehouse', 'Utility Warehouse', '5A2D82')],
  [/\bzen internet\b/, brand('zen', 'Zen Internet', '1D3C6E')],
  [/\bcuckoo\b/, brand('cuckoo', 'Cuckoo', 'FFD100')],
  [/\byoufibre\b/, brand('youfibre', 'YouFibre', 'E6007E')],

  // Energy and water.
  [/\boctopus\b/, brand('octopus', 'Octopus Energy', 'E235B7')],
  [/\bbritish gas\b/, brand('britishgas', 'British Gas', '0079C1')],
  [/\be ?on next\b|\be ?on energy\b/, brand('eonnext', 'E.ON Next', 'EA1C0A')],
  [/\bovo\b/, brand('ovo', 'OVO', '0AA37F')],
  [/\bedf\b/, brand('edf', 'EDF', 'FE5815')],
  [/\bscottish ?power\b/, brand('scottishpower', 'ScottishPower', '5CB85C')],
  [/\butilita\b/, brand('utilita', 'Utilita', 'E6007E')],
  [/\bunited utilities\b/, brand('unitedutilities', 'United Utilities', '0056A4')],
  [/\bthames water\b/, brand('thameswater', 'Thames Water', '005EB8')],
  [/\bsevern trent\b|\bst water\b/, brand('severntrent', 'Severn Trent', '004B87')],
  [/\byorkshire water\b/, brand('yorkshirewater', 'Yorkshire Water', '0072CE')],
  [/\banglian water\b/, brand('anglianwater', 'Anglian Water', '005EB8')],
  [/\bso energy\b/, brand('soenergy', 'So Energy', '1E1E1E')],
  [/\becotricity\b/, brand('ecotricity', 'Ecotricity', '00A651')],
  [/\bsouthern water\b/, brand('southernwater', 'Southern Water', '0072CE')],
  [/\bsouth west water\b/, brand('southwestwater', 'South West Water', '0072CE')],
  [/\bwelsh water\b|\bdwr cymru\b/, brand('welshwater', 'Welsh Water', '0072CE')],
  [/\bnorthumbrian water\b/, brand('northumbrianwater', 'Northumbrian Water', '0072CE')],
  [/\bgood energy\b/, brand('goodenergy', 'Good Energy', '00A651')],
  [/\boutfox\b/, brand('outfox', 'Outfox the Market', 'F39200')],
  [/\bwessex water\b/, brand('wessexwater', 'Wessex Water', '0072CE')],
  [/\baffinity water\b/, brand('affinitywater', 'Affinity Water', '0072CE')],
  [/\bscottish water\b/, brand('scottishwater', 'Scottish Water', '0072CE')],
  [/\bveolia\b/, brand('veolia', 'Veolia', 'E4002B')],

  // Banks, cards, investing and sending money.
  [/\blloyds\b/, brand('lloyds', 'Lloyds', '006A4D')],
  [/\bhalifax\b/, brand('halifax', 'Halifax', '005EB8')],
  [/\bsantander\b/, brand('santander', 'Santander', 'EC0000')],
  [/\bbarclay/, brand('barclays', 'Barclays', '00AEEF')],
  [/\bhsbc\b/, brand('hsbc', 'HSBC', 'DB0011')],
  [/\bfirst ?direct\b/, brand('firstdirect', 'first direct', '1E1E1E')],
  [/\bnatwest\b/, brand('natwest', 'NatWest', '5A287D')],
  [/\bnationwide\b/, brand('nationwide', 'Nationwide', '1C2E5E')],
  [/\btsb\b/, brand('tsb', 'TSB', '00A5E3')],
  [/\bmetro ?bank\b/, brand('metrobank', 'Metro Bank', 'E4002B')],
  [/\bvirgin ?money\b/, brand('virginmoney', 'Virgin Money', 'CC0033')],
  [/\bmonzo\b/, brand('monzo', 'Monzo', '14233C')],
  [/\brevolut\b/, brand('revolut', 'Revolut', '191C1F')],
  [/\bstarling\b/, brand('starling', 'Starling', '6935D3')],
  [/\bchase\b/, brand('chase', 'Chase', '117ACA')],
  [/\bzopa\b/, brand('zopa', 'Zopa', '1E1E1E')],
  [/\bamerican express\b|\bamex\b/, brand('amex', 'American Express', '2E77BC')],
  [/\bcapital ?one\b/, brand('capitalone', 'Capital One', '004977')],
  [/\bvanquis\b/, brand('vanquis', 'Vanquis', '1E1E1E')],
  [/\bpaypal\b/, brand('paypal', 'PayPal', '003087')],
  [/\bklarna\b/, brand('klarna', 'Klarna', 'FFA8CD')],
  [/\bclearpay\b/, brand('clearpay', 'Clearpay', 'B2FCE4')],
  [/\btransfer ?wise\b|\bwise (payments|com|europe)\b|^wise\b/, brand('wise', 'Wise', '9FE870')],
  [/\bwestern ?union\b/, brand('westernunion', 'Western Union', 'FFDD00')],
  [/\bremitly\b/, brand('remitly', 'Remitly', '1E1E1E')],
  [/\bworld ?remit\b/, brand('worldremit', 'WorldRemit', '6C2C91')],
  [/trading ?212/, brand('trading212', 'Trading 212', '1F63E8')],
  [/\bfreetrade\b/, brand('freetrade', 'Freetrade', '1E1E1E')],
  [/\bvanguard\b/, brand('vanguard', 'Vanguard', '96151D')],
  [/\bhargreaves\b/, brand('hargreaves', 'Hargreaves Lansdown', '1E1E1E')],
  [/\bmoneybox\b/, brand('moneybox', 'Moneybox', '1E1E1E')],
  [/\betoro\b/, brand('etoro', 'eToro', '13C636')],
  [/\bcoinbase\b/, brand('coinbase', 'Coinbase', '0052FF')],
  [/\bbinance\b/, brand('binance', 'Binance', 'F0B90B')],
  [/\bkraken\b/, brand('kraken', 'Kraken', '5741D9')],
  [/\bcrypto ?com\b/, brand('cryptocom', 'Crypto.com', '103F68')],
  [/\bnational lottery\b|\blotto\b/, brand('nationallottery', 'National Lottery', 'E4003A')],
  [/\bns ?& ?i\b|\bnational savings\b|\bpremium bonds\b/, brand('nsandi', 'NS&I', '6C2C91')],
  [/\bzilch\b/, brand('zilch', 'Zilch', '1E1E1E')],
  [/\bcoventry (building society|bs)\b/, brand('coventrybs', 'Coventry Building Society', '1D3C6E')],
  [/\byorkshire (building society|bs)\b|\bybs\b/, brand('yorkshirebs', 'Yorkshire Building Society', '1D3C6E')],
  [/\bskipton (building society|bs)\b/, brand('skipton', 'Skipton', '1D3C6E')],
  [/\bbank of scotland\b/, brand('bankofscotland', 'Bank of Scotland', '1D3C6E')],
  [/\brbs\b|\broyal bank of scotland\b/, brand('rbs', 'Royal Bank of Scotland', '1D3C6E')],
  [/\batom bank\b/, brand('atombank', 'Atom bank', '1E1E1E')],
  [/\bkroo\b/, brand('kroo', 'Kroo', '1E1E1E')],
  [/\bgetchip\b|\bchip (financial|savings)\b/, brand('chip', 'Chip', '1E1E1E')],
  [/\bwithplum\b|\bplum (fintech|savings)\b/, brand('plum', 'Plum', '5A2D82')],
  [/\bnutmeg\b/, brand('nutmeg', 'Nutmeg', '1D3C6E')],
  [/\baj bell\b/, brand('ajbell', 'AJ Bell', '1D3C6E')],
  [/\bfidelity\b/, brand('fidelity', 'Fidelity', '368727')],
  [/\binteractive investor\b/, brand('interactiveinvestor', 'interactive investor', '1D3C6E')],
  [/\bmbna\b/, brand('mbna', 'MBNA', '1D3C6E')],
  [/\bjaja\b/, brand('jaja', 'Jaja', '1E1E1E')],
  [/\baqua ?card\b/, brand('aquacard', 'Aqua Card', '00A0DF')],
  [/\bmarbles (card|credit)\b/, brand('marbles', 'Marbles', '5A2D82')],
  [/\bblack horse (finance|ltd|limited)\b/, brand('blackhorse', 'Black Horse', '1E1E1E')],
  [/\bmoneybarn\b/, brand('moneybarn', 'Moneybarn', '00A651')],
  [/\bclose brothers\b/, brand('closebrothers', 'Close Brothers', '1D3C6E')],
  [/\bnovuna\b/, brand('novuna', 'Novuna', '1D3C6E')],
  [/\blendable\b/, brand('lendable', 'Lendable', '1E1E1E')],
  [/\btransfer ?go\b/, brand('transfergo', 'TransferGo', '0A6CFF')],
  [/\bpaysend\b/, brand('paysend', 'Paysend', '1E1E1E')],

  // Insurance.
  [/\baviva\b/, brand('aviva', 'Aviva', 'FFD900')],
  [/\badmiral\b/, brand('admiral', 'Admiral', '1E1E1E')],
  [/\bdirect ?line\b/, brand('directline', 'Direct Line', 'E4002B')],
  [/^churchill\b|\bchurchill (insurance|ins|motor|home)\b/, brand('churchill', 'Churchill', '0055A5')],
  [/\bliverpool victoria\b|^lv\b|\blv (insurance|ins|general)\b/, brand('lv', 'LV=', '00A651')],
  [/\baxa\b/, brand('axa', 'AXA', '00008F')],
  [/\bhastings (direct|insurance|ins)\b/, brand('hastings', 'Hastings Direct', '1E1E1E')],
  [/\blegal ?(&|and) ?general\b/, brand('legalgeneral', 'Legal & General', '0072CE')],
  [/\bvitality\b/, brand('vitality', 'Vitality', 'E6007E')],
  [/\bbupa\b/, brand('bupa', 'Bupa', '0079C8')],
  [/\bcompare ?the ?market\b/, brand('comparethemarket', 'Compare the Market', '1D3C6E')],
  [/\besure\b/, brand('esure', 'esure', 'E4002B')],
  [/\bsheilas? ?wheels\b/, brand('sheilaswheels', 'Sheilas’ Wheels', 'E6007E')],
  [/\b1st central\b|\bfirst central\b/, brand('firstcentral', '1st CENTRAL', '1D3C6E')],
  [/\bmore ?than (insurance|ins)\b|\bmorethan\b/, brand('morethan', 'More Than', '00A651')],
  [/^saga\b|\bsaga (services|insurance|holidays|ltd)\b/, brand('saga', 'Saga', '1D3C6E')],
  [/\bzurich (insurance|assurance|uk)\b/, brand('zurich', 'Zurich', '2167AE')],
  [/\bpetplan\b/, brand('petplan', 'Petplan', '00A0DF')],
  [/\banimal friends\b/, brand('animalfriends', 'Animal Friends', '00843D')],
  [/\bmany ?pets\b|\bbought by many\b/, brand('manypets', 'ManyPets', '1E1E1E')],
  [/\bsimply ?health\b/, brand('simplyhealth', 'Simplyhealth', '5A2D82')],
  [/\bmarshmallow\b/, brand('marshmallow', 'Marshmallow', 'E6007E')],
  [/\bveygo\b/, brand('veygo', 'Veygo', '1E1E1E')],
  [/\bcuvva\b/, brand('cuvva', 'Cuvva', '1E1E1E')],
  [/\bby miles\b/, brand('bymiles', 'By Miles', '1E1E1E')],
  [/\bgo ?compare\b/, brand('gocompare', 'GoCompare', '1D3C6E')],
  [/\bmoney ?super ?market\b/, brand('moneysupermarket', 'MoneySuperMarket', '1E1E1E')],
  [/\bconfused ?com\b/, brand('confused', 'Confused.com', '1E1E1E')],

  // Gyms and cinemas.
  [/\bpuregym\b|\bpure gym\b/, brand('puregym', 'PureGym', '00B2E3')],
  [/\bthe gym group\b|\bthe gym\b/, brand('thegymgroup', 'The Gym Group', '1E1E1E')],
  [/\bnuffield\b/, brand('nuffield', 'Nuffield Health', '00A19A')],
  [/\banytime fitness\b/, brand('anytimefitness', 'Anytime Fitness', '5E2D91')],
  [/\bjd ?gyms?\b/, brand('jdgyms', 'JD Gyms', '1E1E1E')],
  [/\beverlast (gyms?|fitness)\b/, brand('everlastgyms', 'Everlast Gyms', '1E1E1E')],
  [/\beveryman\b/, brand('everyman', 'Everyman', '1E1E1E')],
  [/\bbetter (gym|leisure)\b|\bgll\b/, brand('better', 'Better', '00843D')],
  [/\beveryone active\b/, brand('everyoneactive', 'Everyone Active', 'E6007E')],
  [/\bfitness first\b/, brand('fitnessfirst', 'Fitness First', 'E4002B')],
  [/\bsnap fitness\b/, brand('snapfitness', 'Snap Fitness', '1E1E1E')],
  [/\bvirgin active\b/, brand('virginactive', 'Virgin Active', 'E4002B')],
  [/\bbannatyne\b/, brand('bannatyne', 'Bannatyne', '1D3C6E')],
  [/\bpicturehouse\b/, brand('picturehouse', 'Picturehouse', '1E1E1E')],
  [/\bcurzon\b/, brand('curzon', 'Curzon', '1E1E1E')],
  [/\bomniplex\b/, brand('omniplex', 'Omniplex', '1E1E1E')],
  [/\bticketmaster\b/, brand('ticketmaster', 'Ticketmaster', '026CDF')],
  [/\bsee tickets\b/, brand('seetickets', 'See Tickets', '1E1E1E')],
  [/\beventbrite\b/, brand('eventbrite', 'Eventbrite', 'F05537')],
  [/\baxs\b/, brand('axs', 'AXS', '0066B3')],
  [/\bstubhub\b/, brand('stubhub', 'StubHub', '3F1D74')],
  [/\bviagogo\b/, brand('viagogo', 'viagogo', '00843D')],
  [/\bdice fm\b/, brand('dice', 'DICE', '1E1E1E')],
  [/\bskiddle\b/, brand('skiddle', 'Skiddle', '1E1E1E')],
  [/\bmerlin (entertainments|annual)\b|\balton towers\b|\blegoland\b|\bchessington\b|\bthorpe park\b|\bmadame tussauds\b|\bsea life\b/, brand('merlin', 'Merlin (Alton Towers, Legoland)', '1D3C6E')],
  [/\bhollywood bowl\b/, brand('hollywoodbowl', 'Hollywood Bowl', 'E4002B')],
  [/\btenpin\b/, brand('tenpin', 'Tenpin', '1D3C6E')],
  [/\bnational trust\b/, brand('nationaltrust', 'National Trust', '1E1E1E')],
  [/\benglish heritage\b/, brand('englishheritage', 'English Heritage', '8B1E3F')],
  [/\btopgolf\b/, brand('topgolf', 'Topgolf', '1D3C6E')],
  [/\bgo ape\b/, brand('goape', 'Go Ape', '00843D')],
  [/\bbooksy\b/, brand('booksy', 'Booksy', '1E1E1E')],
  [/\bmy ?dentist\b/, brand('mydentist', 'mydentist', '00A19A')],
  [/\bdenplan\b/, brand('denplan', 'Denplan', '1D3C6E')],
  [/\bwell pharmacy\b/, brand('wellpharmacy', 'Well Pharmacy', '00843D')],
  [/\bodeon\b/, brand('odeon', 'Odeon', '1E1E1E')],
  [/\bvue\b/, brand('vue', 'Vue', '1E1E1E')],
  [/\bcineworld\b/, brand('cineworld', 'Cineworld', '1E1E1E')],
  [/\bshowcase (cinemas?|de lux)\b/, brand('showcase', 'Showcase', '1E1E1E')],

  // Parcels.
  [/\broyal mail\b/, brand('royalmail', 'Royal Mail', 'DA202A')],
  [/\bparcel ?force\b/, brand('parcelforce', 'Parcelforce', 'DA202A')],
  [/\bevri\b|\bhermes\b/, brand('evri', 'Evri', '007BC4')],
  [/\bdpd\b/, brand('dpd', 'DPD', 'DC0032')],
  [/\byodel\b/, brand('yodel', 'Yodel', '1E1E1E')],
  [/\bups\b/, brand('ups', 'UPS', '150400')],
  [/\bdhl\b/, brand('dhl', 'DHL', 'FFCC00')],
  [/\bfed ?ex\b/, brand('fedex', 'FedEx', '4D148C')],
  [/\binpost\b/, brand('inpost', 'InPost', 'FFCD00')],
  [/\bparcel ?2 ?go\b/, brand('parcel2go', 'Parcel2Go', '00A0DF')],
  [/\bpacklink\b/, brand('packlink', 'Packlink', '00A0DF')],
  [/\bgls\b/, brand('gls', 'GLS', '061AB1')],

  // Public bodies and giving.
  [/\bdwp\b|\bhmrc\b|\bdvla\b|\bgov ?uk\b|\bdart ?charge\b|\bhm passport\b|\bhmpo\b|\bhome office\b|\bukvi\b/, brand('govuk', 'GOV.UK', '1D70B8')],
  [/\bnhs\b|\bnhsbsa\b/, brand('nhs', 'NHS', '005EB8')],
  [/\bbury (mbc|council|gov)\b/, brand('councilbury', 'Bury Council', 'F7B500')],
  [/\bjust ?giving\b/, brand('justgiving', 'JustGiving', 'AD29B6')],
  [/\bgo ?fund ?me\b/, brand('gofundme', 'GoFundMe', '00B964')],
  [/\bcancer research\b/, brand('cancerresearch', 'Cancer Research UK', '2E008B')],
  [/\bbhf\b|\bbritish heart foundation\b/, brand('bhf', 'British Heart Foundation', 'E4002B')],
  [/\boxfam\b/, brand('oxfam', 'Oxfam', '44A12B')],
  [/\bred cross\b/, brand('britishredcross', 'British Red Cross', 'E4002B')],
  [/\bmacmillan\b/, brand('macmillan', 'Macmillan', '00843D')],
  [/\brspca\b/, brand('rspca', 'RSPCA', '1D3C6E')],
  [/\bsave the children\b/, brand('savethechildren', 'Save the Children', 'E4002B')],
  [/\bbarnardo/, brand('barnardos', 'Barnardo’s', '1D3C6E')],
  [/\bage uk\b/, brand('ageuk', 'Age UK', '5A2D82')],

  // Poland.
  [/\bbiedronka\b/, brand('biedronka', 'Biedronka', 'E30613')],
  [/\bzabka\b/, brand('zabka', 'Żabka', '006420')],
  [/\bkaufland\b/, brand('kaufland', 'Kaufland', 'E10915', MARKS.siKaufland)],
  [/\brossmann\b/, brand('rossmann', 'Rossmann', 'C3002D')],
  [/\ballegro\b/, brand('allegro', 'Allegro', 'FF5A00')],
  [/\borlen\b/, brand('orlen', 'Orlen', 'E30613')],
  [/\bpko\b/, brand('pkobp', 'PKO BP', '003574')],
  [/\bmbank\b/, brand('mbank', 'mBank', '1E1E1E')],
  [/\bnetto\b/, brand('netto', 'Netto', 'FFE500')],
  [/^dino\b|\bdino (polska|market)\b|\bmarket ?dino\b/, brand('dino', 'Dino', 'E30613')],
  [/\bstokrotka\b/, brand('stokrotka', 'Stokrotka', '00A651')],
  [/\blewiatan\b/, brand('lewiatan', 'Lewiatan', 'E30613')],
  [/\bpolo ?market\b/, brand('polomarket', 'POLOmarket', 'E30613')],
  [/\bauchan\b/, brand('auchan', 'Auchan', 'E2001A')],
  [/\bcarrefour\b/, brand('carrefour', 'Carrefour', '004E9F')],
  [/\bhebe\b/, brand('hebe', 'Hebe', 'E5007D')],
  [/\bempik\b/, brand('empik', 'Empik', 'E30613')],
  [/\bmedia ?markt\b/, brand('mediamarkt', 'MediaMarkt', 'DF0000')],
  [/\brtv ?euro ?agd\b|\beuro com pl\b/, brand('rtveuroagd', 'RTV Euro AGD', '1D3C6E')],
  [/\bmedia ?expert\b/, brand('mediaexpert', 'Media Expert', 'FFD500')],
  [/\bx-?kom\b/, brand('xkom', 'x-kom', '1E1E1E')],
  [/\bcastorama\b/, brand('castorama', 'Castorama', '0078D7')],
  [/\bleroy ?merlin\b/, brand('leroymerlin', 'Leroy Merlin', '78BE20')],
  [/^obi\b|\bobi (market|sp|polska|\d)/, brand('obi', 'OBI', 'F39200')],
  [/\bpepco\b/, brand('pepco', 'Pepco', '1E1E1E')],
  [/\bsinsay\b/, brand('sinsay', 'Sinsay', '1E1E1E')],
  [/^reserved\b|\breserved (sklep|store|pl|\d)/, brand('reserved', 'Reserved', '1E1E1E')],
  [/\bccc\b/, brand('ccc', 'CCC', 'E30613')],
  [/\bsmyk\b/, brand('smyk', 'Smyk', 'E30613')],
  [/\bzalando\b/, brand('zalando', 'Zalando', 'FF6900')],
  [/\bolx\b/, brand('olx', 'OLX', '002F34')],
  [/\bpyszne\b/, brand('pyszne', 'Pyszne.pl', 'FF8000')],
  [/\bglovo\b/, brand('glovo', 'Glovo', 'FFC244')],
  [/\bwolt\b/, brand('wolt', 'Wolt', '00C2E8')],
  [/\bcircle ?k\b/, brand('circlek', 'Circle K', 'DA291C')],
  [/\bmoya\b/, brand('moya', 'Moya', '1E1E1E')],
  [/\bintercity\b|\bpkp ic\b/, brand('pkpintercity', 'PKP Intercity', '1D3C6E')],
  [/\bkoleo\b/, brand('koleo', 'Koleo', '1E1E1E')],
  [/\bjakdojade\b/, brand('jakdojade', 'Jakdojade', 'F9B000')],
  [/\bskycash\b/, brand('skycash', 'SkyCash', '0072BC')],
  [/\borange (polska|pl|sa)\b/, brand('orange', 'Orange', 'FF7900')],
  [/\bp4 sp\b|\bplay ?24\b|\bplay pl\b/, brand('play', 'Play', '5A2D82')],
  [/\bpolkomtel\b|\bplus (gsm|pl)\b/, brand('plus', 'Plus', '00A651')],
  [/\bt-?mobile\b/, brand('tmobile', 'T-Mobile', 'E20074')],
  [/\bpolsat\b/, brand('polsatbox', 'Polsat Box', 'E30613')],
  [/\bpekao\b/, brand('pekao', 'Pekao', 'D71920')],
  [/^ing\b|\bing bank\b/, brand('ing', 'ING', 'FF6200')],
  [/\bbank millenn?ium\b|\bmillenn?ium bank\b/, brand('millennium', 'Millennium', 'C30C4A')],
  [/\balior\b/, brand('alior', 'Alior Bank', 'A6192E')],
  [/\bcredit agricole\b/, brand('creditagricole', 'Credit Agricole', '006A4D')],
  [/\bvelo ?bank\b/, brand('velobank', 'VeloBank', '00A651')],
  [/\bpge\b/, brand('pge', 'PGE', '0066B3')],
  [/\btauron\b/, brand('tauron', 'Tauron', 'E2007A')],
  [/\benea\b/, brand('enea', 'Enea', '00A0E3')],
  [/\benerga\b/, brand('energa', 'Energa', '1D3C6E')],
  [/\bpgnig\b/, brand('pgnig', 'PGNiG', '0072BC')],
  [/\bpoczta polska\b|\bpocztex\b/, brand('pocztapolska', 'Poczta Polska', 'E30613')],
  [/\bzus\b|zaklad ubezpieczen spolecznych/, brand('zus', 'ZUS', '00843D')],
  [/\bconsulate general (of )?poland\b|\bpolish (consulate|embassy)\b|\bkonsulat\b|\bambasada rp\b|\bgov pl\b/, brand('govpl', 'Urzędy RP (gov.pl)', 'DC143C')],
  [/\bsuper ?-?pharm\b/, brand('superpharm', 'Super-Pharm', 'E4002B')],
  [/\bapteka gemini\b|\bgemini apteka\b/, brand('aptekagemini', 'Apteka Gemini', '00843D')],
  [/\bdoz pl\b|\bapteka doz\b/, brand('doz', 'DOZ.pl', '00843D')],
  [/\bdouglas (polska|cosmetics|perfumer)/, brand('douglas', 'Douglas', '1E1E1E')],
  [/\bnotino\b/, brand('notino', 'Notino', '1E1E1E')],
  [/\banswear\b/, brand('answear', 'Answear', '1E1E1E')],
  [/\bmodivo\b/, brand('modivo', 'MODIVO', '1E1E1E')],
  [/\beobuwie\b/, brand('eobuwie', 'eobuwie', 'E4002B')],
  [/\bbonprix\b/, brand('bonprix', 'bonprix', '1E1E1E')],
  [/\b4f\b/, brand('fourf', '4F', '1E1E1E')],
  [/\bcropp\b/, brand('cropp', 'Cropp', '1E1E1E')],
  [/\bmohito\b/, brand('mohito', 'Mohito', '1E1E1E')],
  [/\bwittchen\b/, brand('wittchen', 'Wittchen', '1E1E1E')],
  [/\bw ?kruk\b/, brand('wkruk', 'W.KRUK', '1E1E1E')],
  [/\bblack red white\b|\bbrw\b/, brand('blackredwhite', 'Black Red White', 'E4002B')],
  [/\bagata (meble|s a|sa)\b/, brand('agatameble', 'Agata Meble', 'E4002B')],
  [/\bneonet\b/, brand('neonet', 'Neonet', 'E4002B')],
  [/\bkomputronik\b/, brand('komputronik', 'Komputronik', 'E4002B')],
  [/\bmorele\b/, brand('morele', 'Morele', 'E4002B')],
  [/\btelepizza\b/, brand('telepizza', 'Telepizza', 'E4002B')],
  [/\bda grasso\b/, brand('dagrasso', 'Da Grasso', '00843D')],
  [/\bnorth fish\b/, brand('northfish', 'North Fish', '1D3C6E')],
  [/\bheyah\b/, brand('heyah', 'Heyah', 'E4002B')],
  [/\bnju\b/, brand('nju', 'nju', '1E1E1E')],
  [/\bnetia\b/, brand('netia', 'Netia', 'E4002B')],
  [/\bvectra\b/, brand('vectra', 'Vectra', '0072BC')],
  [/\bviaplay\b/, brand('viaplay', 'Viaplay', '1E1E1E')],
  [/\bbnp paribas\b/, brand('bnpparibas', 'BNP Paribas', '00965E')],
  [/\bnest ?bank\b/, brand('nestbank', 'Nest Bank', '00A0DF')],
  [/\bciti ?handlowy\b|\bcitibank\b/, brand('citihandlowy', 'Citi Handlowy', '0066B3')],
  [/\bwalutomat\b|\bcurrency one\b/, brand('walutomat', 'Walutomat', '1D3C6E')],
  [/\bpzu\b/, brand('pzu', 'PZU', '1D3C6E')],
  [/\bwarta\b/, brand('warta', 'Warta', '1E1E1E')],
  [/\bergo hestia\b|\bhestia\b/, brand('ergohestia', 'Ergo Hestia', 'E4002B')],
  [/\blink4\b/, brand('link4', 'LINK4', 'E4002B')],
  [/\bpolregio\b/, brand('polregio', 'POLREGIO', 'E4002B')],
  [/\bkoleje mazowieckie\b/, brand('kolejemazowieckie', 'Koleje Mazowieckie', '00843D')],
  [/\bitaxi\b/, brand('itaxi', 'iTaxi', 'FFD100')],
  [/\bpanek\b/, brand('panek', 'Panek CarSharing', '00843D')],
  [/\bautopay\b/, brand('autopay', 'Autopay', '1D3C6E')],
  [/\bsindbad\b/, brand('sindbad', 'Sindbad', 'E4002B')],

  // The household's own: the letting agent and the employer.
  [/\bclive anthony\b/, brand('cliveanthony', 'Clive Anthony', '7AB648')],
  [/\bcranswick\b/, brand('cranswick', 'Cranswick', 'B8860B')],
];

/** The brand behind a merchant, a bank text or an institution name, if it is a known one. */
export function brandFor(...texts: (string | null | undefined)[]): Brand | null {
  const text = texts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9&*\- ]+/g, ' ');
  // A LINK cash machine names the shop it stands in ("LNK ASDA BURY"): cash, not the shop.
  if (!text.trim() || /^\s*lnk\b/.test(text)) return null;
  return BRANDS.find(([pattern]) => pattern.test(text))?.[1] ?? null;
}

/** Every known brand once, in the table's order (the list of recognised shops). */
export const KNOWN_BRANDS: readonly Brand[] = [...new Set(BRANDS.map(([, b]) => b))];
